import React, { useState } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { AlertColor, TYPE_FOLDER } from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";
import { FileData } from "util/interfaces/FileData";

import { Alerts } from "../../Alerts/Alerts";
import StepTwo from "../OverrideModal/StepTwo";

import CreateFolderStep from "./CreateFolderStep";
import StepOne from "./StepOne";
import StepThree from "./StepThree";
import { createUploadRequest } from "./UploadFile";
import { getManualConversionStatus } from "@/api/useGetManualConversionStatus";
import { usePostUploadFileOld } from "@/api/usePostUploadFileOld";
import { PENDING_STATUS, UPLOAD_TASK, useBackground } from "@/context/BackgroundContext";
import { getBlobTree } from "@/pages/DocumentManagementView/DocumentGrid.utils";
import { BaseTask } from "@/util/interfaces/background/BaseTask";

import "./uploadModal.css";

interface UploadFileModalProps {
  callbackClose: (_updateData: boolean, _numFiles?: number) => void;
  documents: DocumentData[];
  showModal: "upload_file" | "create_folder" | null;
  selectedPath: { id: string; name: string }[];
}

interface ISteps {
  subtitle: string;
  body: React.ReactNode;
  key: string;
}

export const UploadFileModal: React.FC<UploadFileModalProps> = ({
  callbackClose,
  documents,
  showModal,
  selectedPath,
}) => {
  const { addBackgroundTask } = useBackground();
  const { mutateAsync: callUploadFile } = usePostUploadFileOld();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [duplicateStyle, setDuplicateStyle] = useState({
    top: "90%",
    right: "unset",
    left: "unset",
  });
  const [disabledNext, setDisabledNext] = useState<boolean>(true);
  const [files, setFiles] = useState<FileData[]>([]);
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [folderPath, setFolderPath] = useState<string>("");
  const STEPS: ISteps[] = [];
  const tree = getBlobTree(documents);
  if (showModal === "create_folder") {
    STEPS.push({
      key: "create_folder",
      subtitle: "Folder name",
      body: (
        <CreateFolderStep
          folderPath={folderPath}
          onChangeCallback={(e): boolean => {
            const value = e.target.value;
            // Lets validate if the folder exists
            let currentParentId: string | null = null;
            for (const pathSegment of selectedPath) {
              const foundFolder = tree.find(
                (doc) =>
                  doc.documentName === pathSegment.name &&
                  doc.parentId === currentParentId &&
                  doc.fileType === TYPE_FOLDER
              );
              if (foundFolder) {
                currentParentId = foundFolder.id;
              }
            }
            const includes = tree.some(
              (doc) => doc.parentId === currentParentId && doc.documentName === value
            );
            setFolderPath(value);
            setDisabledNext(!value.trim() || includes);
            return includes;
          }}
        />
      ),
    });
  }

  STEPS.push(
    {
      key: "drag",
      subtitle:
        'Drag and drop your files into this area to upload or select "Browse File" to select from your desktop.',
      body: (
        <StepOne
          documents={documents}
          existingFiles={files}
          selectedPath={[...selectedPath, { id: `constructed-${folderPath}`, name: folderPath }]}
          callbackError={(msg, alertColor, disable = true) => {
            setAlertColor(alertColor);
            setAlertMessage(msg);
            setDisabledNext(disable);
            setDuplicateStyle({ top: "", right: "", left: "" });
          }}
          callbackSetFiles={(files) => {
            setFiles(
              files.map((f) => ({
                file: f.file,
                fileCategory: f.fileCategory ?? "",
                metadata: f.metadata ?? "",
                override: f.override,
                isCompleted: f.isCompleted ?? false,
                isChecked: f.isChecked ?? false,
                uuid: f.uuid,
              }))
            );
            setDisabledNext(files.length === 0 || files.some((f) => f.duplicated));
          }}
        />
      ),
    },
    {
      key: "details",
      subtitle:
        'Select document on the left-hand side then add document details on the right. click "Save" when completed for all file attachments.',
      body: (
        <StepTwo
          files={files}
          callbackSetMessage={(message: string) => {
            setAlertColor(AlertColor.Success);
            setAlertMessage(message);
          }}
          callbackSetFiles={(processedFiles: FileData[]) => {
            setDisabledNext(processedFiles.filter((f) => f.isCompleted).length !== files.length);
            setFiles(processedFiles);
          }}
        />
      ),
    },
    {
      key: "review",
      subtitle: "Review and confirm document information.",
      body: <StepThree files={files} />,
    }
  );

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const selectedStep = STEPS[currentStepIndex];

  const getTextOfButton = () => {
    if (currentStepIndex === STEPS.length - 1) {
      if (isLoading) {
        return (
          <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
            <CircularProgress size={24} />
            <span style={{ marginLeft: 10 }}>Uploading...</span>
          </div>
        );
      }
      return "Upload";
    }

    if (selectedStep.key === "create_folder") {
      return "Add";
    }

    return "Next";
  };
  const getTextOfCancelButton = (currentStepIndex: number) => {
    if (currentStepIndex === 0) {
      return "Cancel";
    }
    return "Previous";
  };

  const createBackgroundTask = (file: FileData): BaseTask => {
    const sendData = createUploadRequest([file]);
    return {
      id: "", //Placeholder for id
      status: PENDING_STATUS,
      promise: callUploadFile(sendData),
      intervalId: setInterval(() => {}, 1000), // Placeholder, will be updated below
      createdAt: new Date(),
      file: { name: file.file.name, id: file.uuid ?? "" },
      pollingConfig: {
        interval: 5000,
        shouldStartPolling: (response: any) => response?.data?.result[0]?.batch_id,
        pollingFunction: (response) =>
          getManualConversionStatus(response?.data?.result[0]?.batch_id),
        shouldContinuePolling: (response: any) => {
          if (response.httpStatus !== 200) return false;
          const files = response.data?.result[0]?.files;
          return files.some((f: any) => f.status === "IN_PROGRESS");
        },
      },
    };
  };

  const uploadFiles = () => {
    try {
      const filesToUpload: FileData[] = files
        .filter((f) => f.isCompleted)
        .map((item) => ({
          file: item.file,
          fileCategory: item.fileCategory,
          metadata: item.metadata,
          path:
            selectedPath.length === 0
              ? folderPath
              : `${selectedPath.map((p) => p.name).join("/")}/${folderPath}`,
          uuid: item.uuid ?? "",
        }));
      //Let's create the background task
      const backgroundTasks: BaseTask[] = filesToUpload.map(createBackgroundTask);

      addBackgroundTask(backgroundTasks, UPLOAD_TASK);
    } catch {
      setAlertColor(AlertColor.Error);
      setAlertMessage("Unexpected error while trying to upload files into the storage.");
      setIsLoading(false);
      setDisabledNext(false);
      return false;
    }
    return true;
  };
  const handleSubmit = async () => {
    setIsLoading(true);
    setDisabledNext(true);
    uploadFiles();
    callbackClose(true, files.length);
  };

  const title = selectedStep.key === "create_folder" ? "Add Folder" : "File upload";

  const getModalHeight = (key: string) => {
    const keys: Record<string, string> = { review: "auto", create_folder: "auto" };

    return keys[key] ?? "65vh";
  };

  const contentHeight = getModalHeight(selectedStep.key);

  return (
    <>
      <Modal open={!!showModal} onClose={() => callbackClose(false)} data-testid="modalUploadFiles">
        <div
          className="modal-kb-content"
          style={{
            height: contentHeight,
            width: "60vw",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "4px",
          }}
        >
          <div className="modal-kb-header">
            <button onClick={() => callbackClose(false)} className="close_icon">
              <Icon icon="bitcoin-icons:cross-filled" height={30} color="#000000" />
            </button>
            <div className="flex flow-column">
              <span className="upload_title">{title}</span>
              <span>{selectedStep.subtitle}</span>
            </div>
          </div>
          <div
            className="modal-kb-body"
            style={{ overflowY: selectedStep.key === "review" ? "hidden" : "auto" }}
          >
            {selectedStep.body}
          </div>
          <div
            className="modal-kb-footer"
            style={{ marginTop: selectedStep.key === "review" ? "3%" : "" }}
          >
            <button
              className="save_btn"
              data-testid="nextStepButton"
              onClick={() => {
                if (currentStepIndex === STEPS.length - 1) {
                  handleSubmit();
                } else {
                  setCurrentStepIndex((prev) => prev + 1);
                  setDisabledNext(selectedStep.key !== "details");
                }
              }}
              disabled={disabledNext}
            >
              {getTextOfButton()}
            </button>
            <button
              onClick={() => {
                if (currentStepIndex === 0) {
                  callbackClose(false);
                } else {
                  setCurrentStepIndex((prev) => prev - 1);
                }
              }}
              className="cancel_btn"
              data-testid="cancelButton"
              disabled={isLoading}
            >
              {getTextOfCancelButton(currentStepIndex)}
            </button>
          </div>
        </div>
      </Modal>
      <Alerts
        style={duplicateStyle}
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
